import { useState, useEffect } from "react";
import Image from "next/image";
import { toast, ToastContainer } from "react-toastify";
import { getCookie } from "utils/index";
import ShowLoadingModal from "@/components/homes/heros/ShowLoadingModal";
import ResultModal from "@/components/homes/heros/ResultModal";
import LoginPromptModal from "@/components/homes/heros/LoginPromptModal";
import AOS from "aos";
import "aos/dist/aos.css";
import "react-toastify/dist/ReactToastify.css";

// Main NEETRankPredictor Component
const NEETRankPredictor = () => {
  const [marks, setMarks] = useState("");
  const [predictedRanks, setPredictedRanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const authToken = getCookie("authToken");
  const isLoggedIn = !!authToken;

  // ------------------------
  // FAST AOS INIT
  // ------------------------
  useEffect(() => {
    AOS.init({
      duration: 400, // was 1000
      offset: 100,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  // ------------------------
  // FORM SUBMIT
  // ------------------------
  const handleMarksSubmit = (e) => {
    e.preventDefault();

    const marksInt = parseInt(marks, 10);

    if (isNaN(marksInt) || marksInt < 1 || marksInt > 720) {
      toast.error("Please enter a valid NEET score between 1 and 720");
      return;
    }

    if (isLoggedIn) {
      handlePredictRank();
    } else {
      setShowLoginPrompt(true);
    }
  };

  // ------------------------
  // API CALL
  // ------------------------
  const handlePredictRank = async () => {
    const startTime = Date.now();

    setLoading(true);
    setShowLoadingModal(true);
    setPredictedRanks([]);

    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL;

      const response = await fetch(`${API_URL}/api/predict-rank`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(isLoggedIn && { Authorization: `Bearer ${authToken}` }),
        },
        body: JSON.stringify({
          marks: parseInt(marks, 10),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const predictedRank = data.predicted_rank;
      const confidenceLower =
        predictedRank === 1 ? 1 : Math.max(1, Math.round(predictedRank - 25));
      const confidenceUpper = Math.round(predictedRank + 25);

      const confidenceRange = `${confidenceLower} - ${confidenceUpper}`;

      setPredictedRanks([
        {
          marks,
          predictedRank,
          confidenceRange,
          previousYearRanks: data.previousYearRanks || {},
        },
      ]);

      // ------------------------
      // SMART LOADER TIMING
      // ------------------------
      const elapsed = Date.now() - startTime;
      const minDelay = 500; // smooth UX
      const remaining = elapsed < minDelay ? minDelay - elapsed : 0;

      setTimeout(() => {
        setShowLoadingModal(false);
        setShowResultModal(true);
      }, remaining);

    } catch (error) {
      console.error("Error details:", error);
      toast.error("Error predicting rank. Please try again.");
      setShowLoadingModal(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col-reverse gap-0 md:gap-4 lg:gap-4 lg:flex-row p-2 lg:p-10 mb-10 lg:mb-0">

      <ToastContainer />

      {/* LEFT CONTENT */}
      <div
        className="predict-content m-auto w-full px-4 lg:p-5"
        data-aos="fade-right"
        data-aos-delay="120"
      >
        <h2 className="text-[23px] sm:text-3xl lg:text-4xl font-semibold text-[#140342]">
          NEET Rank Predictor <span className="predict-highlight">2026</span>
        </h2>

        <p className="predict-desc text-[15px] md:text-justify font-bold text-[#4F547B]">
          Curious about where you stand after your NEET exam? Our AI-powered
          Rank Predictor gives you an instant estimate of your All India Rank
          based on your score quick, accurate, and stress-free. <br />
          We don’t just predict we empower. By analyzing previous years’ data,
          trends, and cut-off patterns, our tool gives you a clear picture of
          your chances, helping you plan your next move with confidence. <br />
          No more confusion get clear about your NEET Score and plan your next
          step with ease.
        </p>

        <form
          onSubmit={handleMarksSubmit}
          className="predict-input-container text-[15px] flex gap-4 flex-row md:flex-row"
        >
          <input
            type="tel"
            placeholder="Enter Your NEET Score"
            value={marks}
            className="p-2 rounded-md border border-gray-300 w-full md:w-auto focus:outline-none focus:ring-1 focus:ring-[#ff9245] focus:border-[#ff9245]"
            onChange={(e) => setMarks(e.target.value)}
            data-aos="fade-up"
            data-aos-delay="150"
          />

          <button
            type="submit"
            disabled={loading}
            data-aos="fade-up"
            data-aos-delay="180"
            className="p-2 bg-[#ff9245] text-white rounded-md w-full md:w-auto"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>

      {/* MODALS */}
      {showLoadingModal && <ShowLoadingModal show={showLoadingModal} />}

      <ResultModal
        predictedRanks={predictedRanks}
        showResultModal={showResultModal}
        setShowResultModal={setShowResultModal}
      />

      {showLoginPrompt && (
        <LoginPromptModal
          onClose={() => setShowLoginPrompt(false)}
          tag="Rank Predictor"
          marks={marks}
        />
      )}

      {/* RIGHT IMAGE */}
      <div
        className="predict-image-container sm:w-full p-3 sm:px-0 md:block"
        data-aos="fade-left"
        data-aos-delay="150"
      >
        <Image
          src="/assets/img/home/NeetPredict/1.webp"
          alt="Rank_Predictor"
          width={600}
          height={400}
          loading="lazy"
          className="object-cover m-auto w-full max-w-[100%] md:max-w-[500px] lg:max-w-[600px]"
          sizes="(max-width: 600px) 100vw, 600px"
          priority={false}
        />
      </div>

    </div>
  );
};

export default NEETRankPredictor;
